import { useEffect, useMemo, useState } from 'react'

import { PlusIcon, Trash } from 'lucide-react'
import { Button, DefaultTooltip, Input, SliderGradient } from 'twyne-new.ui'

import { parseGradient, useCraftProp } from '@/components/builder/utils'
import { TailwindSandboxPure } from '@/modules/cms/views/post/components/builder/Content/TailwindSandbox'

import ColorPickerProperty from '../../Color'
import ApplyButtonPopover from '../ApplyButtonPopover'

import PickerConfig from './PickerConfig'

const GradientPicker = ({ name, value: valueProp = {}, onChange, onClose }) => {
  const { value: craftValue = {}, setValue: setCraftValue } = useCraftProp(name)
  const [gradient, setGradient] = useState()

  useEffect(() => {
    if (gradient === undefined) {
      const value = name ? craftValue : valueProp
      setGradient(value?.colors ? value : { colors: [{}], type: 'linear', linear: { degrees: 90 } })
    }
  }, [gradient, craftValue, valueProp, name])

  const handleApply = () => {
    if (name) {
      setCraftValue(gradient)
    } else {
      onChange(gradient)
    }
  }

  const handleChangeColorValue = (index, value) => {
    const newColors = [...gradient.colors]
    newColors[index] = { ...newColors[index], value: Number(value) }
    setGradient({ ...gradient, colors: newColors })
  }

  const getColorValue = index => {
    const currentColor = gradient.colors[index]
    const maxValue = Math.max(...gradient.colors.map(color => color?.value || 0))
    const rest = Math.round((100 - maxValue) / 2) + maxValue
    const value = currentColor?.value ? currentColor?.value : !maxValue ? 1 : rest
    return value
  }

  const handleChangeColor = (index, color) => {
    const value = getColorValue(index)
    const newColors = [...gradient.colors]
    newColors[index] = { color, value }
    setGradient({ ...gradient, colors: newColors })
  }

  const handleRemoveColor = index => {
    const newColors = [...gradient?.colors || []]
    newColors.splice(index, 1)
    setGradient({ ...gradient, colors: newColors })
  }

  const handleCancel = () => {
    setGradient()
    onClose()
  }

  if (!gradient) {
    return null
  }

  return <div className='flex flex-col gap-4'>
    <PickerConfig gradient={gradient} onChange={setGradient} />
    <Slider colors={gradient?.colors} onChangeValue={handleChangeColorValue} />
    <Colors
      colors={gradient?.colors}
      onRemoveColor={handleRemoveColor}
      onChangerColorValue={handleChangeColorValue}
      onChangeColor={handleChangeColor}
    />
    <AddButton onAddColor={() => setGradient({ ...gradient, colors: [...gradient?.colors || [], {}] })} />
    <Preview gradient={gradient} />
    <ApplyButtonPopover
      onApply={handleApply}
      onCancel={handleCancel}
      name='background.gradient'
      value={gradient}
    />
  </div>
}

const Slider = ({ colors, onChangeValue }) => <div className='h-[50px]'>
  <TailwindSandboxPure>
    <div className='h-[50px] flex items-center justify-center'>
      <SliderGradient
        values={colors}
        onChange={(index, value) => onChangeValue(index, value)}
        max={100}
        step={1}
      />
    </div>
  </TailwindSandboxPure>
</div>

const Preview = ({ gradient = {} }) => {
  const gradientBackground = useMemo(() => {
    const { type, colors } = gradient
    if (!type || !colors?.length) {
      return ''
    }

    const gradientClass = parseGradient(gradient)
    if (!gradientClass) {
      return ''
    }

    return `bg-[${gradientClass}]`
  }, [gradient])

  if (!gradient || !gradient?.colors?.length || !gradientBackground) {
    return null
  }

  return <>
    <div className='w-full h-[50px]'>
      <TailwindSandboxPure>
        <div className={`rounded-xs h-[50px] w-full ${gradientBackground}`}>
        </div>
      </TailwindSandboxPure >
    </div>
  </>
}

const Colors = ({ colors, onRemoveColor, onChangerColorValue, onChangeColor }) => {
  if (colors.length && typeof colors[0] === 'string') {
    return null
  }

  return <>
    <div className='flex flex-col gap-2'>
      {colors.map(({ color, value }, index) => <div key={index} className='flex gap-2 w-full'>
        <ColorPickerProperty side='top' storeAll value={color} onChange={color => onChangeColor(index, color)} />
        <Input
          value={value || ''}
          onChange={e => onChangerColorValue(index, e.target.value)}
          type='number'
          variant='accent'
          className='w-24'
        />
        <Button variant='outline' size='sm' onClick={() => onRemoveColor(index)}>
          <Trash size='14' />
        </Button>
      </div>)}
    </div>
  </>
}

const AddButton = ({ onAddColor }) => <>
  <div className='flex justify-end items-center'>
    <DefaultTooltip content='Add color' triggerProps={{ asChild: true }}>
      <Button variant='light' size='sm' onClick={onAddColor}>
        <PlusIcon size='14' />
      </Button>
    </DefaultTooltip>
  </div>
</>

export default GradientPicker
